import { Edit, Eye, FileText, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { adminEmailAPI } from '../../../../shared/services/emailAPI'
import type { EmailTemplate } from './types'
import TemplateFormModal from './TemplateFormModal'

export default function EmailTemplatesTab({
  templates,
  loading,
  onDeleteTemplate,
  deleteLoading,
}: {
  templates: EmailTemplate[]
  pagination?: any
  loading: boolean
  onDeleteTemplate: (id: string) => void
  deleteLoading: boolean
}) {
  const [selectedTemplate, setSelectedTemplate] =
    useState<EmailTemplate | null>(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(
    null
  )
  const queryClient = useQueryClient()

  // 템플릿 생성 mutation
  const createTemplateMutation = useMutation({
    mutationFn: adminEmailAPI.createTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emailTemplates'] })
      setShowCreateModal(false)
    },
  })

  // 템플릿 수정 mutation
  const updateTemplateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      adminEmailAPI.updateTemplate(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emailTemplates'] })
      setEditingTemplate(null)
    },
  })

  // 개발중 - 템플릿 생성/수정 기능 활성화 예정
  console.log('Template modal states:', {
    showCreateModal,
    editingTemplate,
    createTemplateMutation,
    updateTemplateMutation,
  })

  if (loading) {
    return (
      <div className="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
        <div className="animate-pulse space-y-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <div
              key={i}
              className="h-20 rounded bg-gray-200 dark:bg-gray-700"
            ></div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            메일 템플릿
          </h3>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-500"
          >
            <Plus className="h-4 w-4" />새 템플릿
          </button>
        </div>
      </div>

      {/* 템플릿 목록 */}
      <div className="rounded-lg bg-white shadow dark:bg-gray-800">
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {templates.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="mx-auto mb-4 h-12 w-12 text-gray-400" />
              <p className="text-gray-500 dark:text-gray-400">
                등록된 템플릿이 없습니다.
              </p>
            </div>
          ) : (
            templates.map(template => (
              <div
                key={template._id}
                className="p-6 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <h4 className="text-lg font-medium text-gray-900 dark:text-white">
                        {template.name}
                      </h4>
                      <div className="flex gap-2">
                        <span className="inline-flex rounded-full bg-blue-100 px-2 py-1 text-xs font-semibold text-blue-800 dark:bg-blue-900/20 dark:text-blue-400">
                          {template.templateType}
                        </span>
                        {template.isSystem && (
                          <span className="inline-flex rounded-full bg-gray-100 px-2 py-1 text-xs font-semibold text-gray-800 dark:bg-gray-900/20 dark:text-gray-400">
                            시스템
                          </span>
                        )}
                        {!template.isActive && (
                          <span className="inline-flex rounded-full bg-red-100 px-2 py-1 text-xs font-semibold text-red-800 dark:bg-red-900/20 dark:text-red-400">
                            비활성
                          </span>
                        )}
                      </div>
                    </div>
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                      {template.description || '설명 없음'}
                    </p>
                    <div className="mt-2 flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                      <span>제목: {template.subject}</span>
                      <span>
                        사용 횟수: {(template.usageCount || 0).toLocaleString()}
                      </span>
                      {template.createdBy && (
                        <span>
                          생성자:{' '}
                          {template.createdBy.name || template.createdBy.email}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setSelectedTemplate(template)}
                      className="p-2 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                      title="상세 보기"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    {/* 시스템 템플릿도 편집 가능 */}
                    <button
                      onClick={() => setEditingTemplate(template)}
                      className="p-2 text-gray-400 hover:text-green-600 dark:hover:text-green-400"
                      title={template.isSystem ? '시스템 템플릿 편집' : '편집'}
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    {/* 시스템 템플릿은 삭제만 제한 */}
                    {!template.isSystem && (
                      <button
                        onClick={() => {
                          if (confirm('이 템플릿을 삭제하시겠습니까?')) {
                            onDeleteTemplate(template._id)
                          }
                        }}
                        disabled={deleteLoading}
                        className="p-2 text-gray-400 hover:text-red-600 disabled:opacity-50 dark:hover:text-red-400"
                        title="삭제"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* 템플릿 상세 모달 */}
      {selectedTemplate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-lg bg-white dark:bg-gray-800">
            <div className="border-b border-gray-200 p-6 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  템플릿 상세: {selectedTemplate.name}
                </h3>
                <button
                  onClick={() => setSelectedTemplate(null)}
                  className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="space-y-4 p-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    템플릿명
                  </label>
                  <p className="mt-1 text-sm text-gray-900 dark:text-white">
                    {selectedTemplate.name}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    타입
                  </label>
                  <p className="mt-1 text-sm text-gray-900 dark:text-white">
                    {selectedTemplate.templateType}
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  제목
                </label>
                <p className="mt-1 text-sm text-gray-900 dark:text-white">
                  {selectedTemplate.subject}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  HTML 내용
                </label>
                <div className="mt-1 rounded-lg bg-gray-50 p-3 dark:bg-gray-700">
                  <pre className="overflow-x-auto whitespace-pre-wrap text-xs text-gray-900 dark:text-white">
                    {selectedTemplate.htmlContent}
                  </pre>
                </div>
              </div>

              {selectedTemplate.variables.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    변수
                  </label>
                  <div className="mt-1 space-y-2">
                    {selectedTemplate.variables.map((variable, index) => (
                      <div
                        key={index}
                        className="rounded bg-gray-50 p-2 dark:bg-gray-700"
                      >
                        <div className="text-sm">
                          <span className="font-medium">{variable.name}</span>
                          <span className="ml-2 text-gray-500">
                            ({variable.type})
                          </span>
                        </div>
                        {variable.description && (
                          <p className="text-xs text-gray-600 dark:text-gray-400">
                            {variable.description}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 새 템플릿 생성 모달 */}
      {showCreateModal && (
        <TemplateFormModal
          title="새 템플릿 생성"
          onSubmit={createTemplateMutation.mutate}
          onClose={() => setShowCreateModal(false)}
          isLoading={createTemplateMutation.isPending}
        />
      )}

      {/* 템플릿 수정 모달 */}
      {editingTemplate && (
        <TemplateFormModal
          title="템플릿 수정"
          template={editingTemplate}
          onSubmit={data =>
            updateTemplateMutation.mutate({ id: editingTemplate._id, data })
          }
          onClose={() => setEditingTemplate(null)}
          isLoading={updateTemplateMutation.isPending}
        />
      )}
    </div>
  )
}
